import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type DayPlan, inferOffset, type TimetablePlanInput } from '@trafiku/contracts';
import { toast } from 'sonner';
import { ApiError, api } from './api';
import { t } from './i18n';
import { positionAlong } from './route';

export type Day = 'week' | 'weekend';

export interface LineTimetableSummary {
  _id: string;
  name: string;
  zone: { _id: string; name: string; bus_sign: string; color: string; direction: 'going' | 'return' } | null;
  stations: number;
  with_times: number;
  week: { count: number; first: string | null; last: string | null };
  weekend: { count: number; first: string | null; last: string | null };
  plan: { updated_at: string; updated_by: string | null } | null;
}

interface LineTimetable {
  line: {
    _id: string;
    name: string;
    stations: Array<{ _id: string; name: string; coordinates: [number, number] | null }>;
  };
  plan: (TimetablePlanInput & { updated_at: string; updated_by: string | null }) | null;
  current: Record<string, { week: string[]; weekend: string[] }>;
}

export function useLineTimetables() {
  return useQuery({
    queryKey: ['timetables', 'lines'],
    queryFn: () => api<{ items: LineTimetableSummary[] }>('/timetables/lines'),
  });
}

export function useLineTimetable(id: string | undefined) {
  return useQuery({
    queryKey: ['timetables', 'line', id],
    queryFn: () => api<LineTimetable>(`/timetables/lines/${id}`),
    enabled: !!id,
  });
}

export function useSaveLineTimetable(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TimetablePlanInput) =>
      api<{ stations: number; removed: number }>(`/timetables/lines/${id}`, { method: 'PUT', body: input }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['timetables'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
      toast.success(t('Timetables saved for {n} stations', { n: r.stations }));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not save')),
  });
}

/** The editor's working copy: per kind of day, and minutes per station (null: not known yet). */
export interface Draft {
  week: DayPlan;
  weekend: DayPlan;
  minutes: Record<string, number | null>;
}

/**
 * Travel time to each station estimated from the route: distance along it at `kmh`, plus
 * `dwell` minutes per stop before it. Whole minutes.
 */
export function estimateMinutes(
  route: number[][] | undefined,
  stations: Array<{ _id: string; coordinates: [number, number] | null }>,
  kmh: number,
  dwell = 0.5,
): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  if (!route || route.length < 2) {
    for (const s of stations) out[s._id] = null;
    return out;
  }
  const start = stations[0]?.coordinates ? positionAlong(route, stations[0].coordinates) : 0;
  stations.forEach((s, i) => {
    if (!s.coordinates) {
      out[s._id] = null;
      return;
    }
    const metres = Math.max(0, positionAlong(route, s.coordinates) - start);
    out[s._id] = i === 0 ? 0 : Math.round((metres / 1000 / kmh) * 60 + i * dwell);
  });
  return out;
}

/** Travel time to each station read from the current timetables (null where they say nothing). */
export function minutesFromCurrent(data: LineTimetable): Record<string, number | null> {
  const stations = data.line.stations;
  const first = data.current[stations[0]?._id ?? ''];
  const out: Record<string, number | null> = {};
  stations.forEach((s, i) => {
    if (i === 0) {
      out[s._id] = 0;
      return;
    }
    const cur = data.current[s._id];
    out[s._id] =
      inferOffset(first?.week ?? [], cur?.week ?? []) ??
      inferOffset(first?.weekend ?? [], cur?.weekend ?? []);
  });
  return out;
}

/** Fills unknown travel times by interpolating between known neighbours (or from `fallback`). */
export function fillMinutes(
  stations: Array<{ _id: string }>,
  known: Record<string, number | null>,
  fallback: Record<string, number | null>,
): Record<string, number | null> {
  const out = { ...known };
  stations.forEach((s, i) => {
    if (out[s._id] != null) return;
    const prev = stations.slice(0, i).findLastIndex((p) => out[p._id] != null);
    const next = stations.findIndex((n, j) => j > i && known[n._id] != null);
    if (prev >= 0 && next >= 0) {
      const a = out[stations[prev]!._id]!;
      const b = known[stations[next]!._id]!;
      out[s._id] = Math.round(a + ((b - a) * (i - prev)) / (next - prev));
    } else out[s._id] = fallback[s._id] ?? null;
  });
  return out;
}

/** Starting point for the editor: the saved plan, or what the current timetables say. */
export function initialDraft(data: LineTimetable, route: number[][] | undefined): Draft {
  const stations = data.line.stations;
  if (data.plan) {
    const saved = Object.fromEntries(data.plan.offsets.map((o) => [String(o.station), o.minutes]));
    return {
      week: data.plan.week,
      weekend: data.plan.weekend,
      minutes: fillMinutes(stations, saved, estimateMinutes(route, stations, 20)),
    };
  }
  const first = data.current[stations[0]?._id ?? ''];
  return {
    week: { patterns: [], departures: first?.week ?? [] },
    weekend: { patterns: [], departures: first?.weekend ?? [] },
    minutes: fillMinutes(stations, minutesFromCurrent(data), estimateMinutes(route, stations, 20)),
  };
}
